import { useEffect, useRef } from 'react';

import { BREADCRUMBS_MODES } from '@confluence/breadcrumbs/entry-points/BreadcrumbsWithSpaceNameDropdown.ts';
import { isCanvasContentType } from '@confluence/content-types-utils/entry-points/contentTypeConstants.ts';
import { confluenceLocalStorageInstance } from '@confluence/storage-manager/entry-points/storageInstance.ts';
import { PERSISTED_KEYS_ON_SERVER } from '@confluence/storage-manager/entry-points/LocalStorageKeys.ts';

const getIsSideNavCollapsed = () => {
	const sideNavCollapsedValue =
		confluenceLocalStorageInstance.getItem(
			PERSISTED_KEYS_ON_SERVER.PERSISTED_LEFT_SIDEBAR_COLLAPSED,
		) ?? 'expanded';
	return sideNavCollapsedValue === 'collapsed';
};

type UseBreadcrumbsDisplayModeType = {
	contentType: string;
	isSpaceOverviewPage: boolean;
	isVersionHistoryPage: boolean;
	isAttachmentsPage: boolean;
	isArchived: boolean;
	isEditorToolbarShown: boolean;
	isTitleFullyVisible?: boolean;
	isLegacyObjectSidebarShown: boolean;
	contentId?: string;
	contentTypesHeaderDataLoading?: boolean;
};

export const useBreadcrumbsDisplayMode = ({
	contentType,
	isSpaceOverviewPage,
	isVersionHistoryPage,
	isAttachmentsPage,
	isArchived,
	isEditorToolbarShown,
	isTitleFullyVisible,
	isLegacyObjectSidebarShown,
	contentId,
	contentTypesHeaderDataLoading,
}: UseBreadcrumbsDisplayModeType): BREADCRUMBS_MODES => {
	const hasMounted = useRef(false);

	// only live pages, page, blogs, and editor (when toolbar is not shown) care about title visibility (when user scrolls past page title)
	// except for space overview and archived pages and version history
	const shouldListenForTitleVisibilityChanges =
		(contentType === 'blogpost' || contentType === 'page') &&
		!isSpaceOverviewPage &&
		!isArchived &&
		!isEditorToolbarShown &&
		!isVersionHistoryPage &&
		!isAttachmentsPage;

	const checkAndSetDisplayAttribute = (element: HTMLElement | null, displayProperty: string) => {
		//consider checking if element is hTMLElement and if not exiting the function
		if (
			element &&
			window.getComputedStyle(element).getPropertyValue('display') !== displayProperty
		) {
			element?.setAttribute('style', `display: ${displayProperty};`);
		}
	};

	const checkAndSetStyle = (element: HTMLElement | null, property: string, value: string) => {
		//consider checking if element is hTMLElement and if not exiting the function
		if (element && element?.style?.[property] !== value) {
			element.style[property] = value;
		}
	};

	// this useEffect duplicates logic for FULL, ONLY_CONTENT_TITLE, ONLY_SPACE_NAME, and HIDDEN breadcrumbs
	// this is required because using react state to conditionally render parts of breadcrumbs is simply not fast enough and causes jumpiness in the breadcrumbs on side nav collapse/expand
	// AND we need to fine-tuned control over fade in of content title breadcrumbs
	useEffect(() => {
		const toggleSpaceNameBreadcrumbListener = () => {
			const separator = document.getElementById('breadcrumbs-separator');
			const spaceName = document.getElementById('breadcrumbs-space-name');
			const chevron = document.getElementById('breadcrumbs-dropdown-chevron-button');
			const contentTitle = document.getElementById('breadcrumbs-content-title');

			const showOnlyContentTitle = () => {
				checkAndSetDisplayAttribute(separator, 'none');
				checkAndSetDisplayAttribute(spaceName, 'none');
				checkAndSetDisplayAttribute(chevron, 'block');
				checkAndSetStyle(contentTitle, 'opacity', '1');
				checkAndSetStyle(contentTitle, 'maxWidth', '800px');
			};

			const showOnlySpaceName = () => {
				checkAndSetDisplayAttribute(separator, 'none');
				checkAndSetDisplayAttribute(spaceName, 'inline');
				checkAndSetDisplayAttribute(chevron, 'block');
				checkAndSetStyle(contentTitle, 'opacity', '0');
				checkAndSetStyle(contentTitle, 'maxWidth', '0px');
			};

			const showFullBreadcrumbs = () => {
				checkAndSetDisplayAttribute(separator, 'inline');
				checkAndSetDisplayAttribute(spaceName, 'inline');
				checkAndSetDisplayAttribute(chevron, 'block');
				checkAndSetStyle(contentTitle, 'opacity', '1');
				checkAndSetStyle(contentTitle, 'maxWidth', '800px');
			};

			const hideEntireBreadcrumbs = () => {
				checkAndSetDisplayAttribute(separator, 'none');
				checkAndSetDisplayAttribute(spaceName, 'none');
				checkAndSetDisplayAttribute(chevron, 'none');
				checkAndSetStyle(contentTitle, 'opacity', '0');
				checkAndSetStyle(contentTitle, 'maxWidth', '0px');
			};

			if (!isEditorToolbarShown) {
				checkAndSetStyle(contentTitle, 'transition', 'opacity 100ms linear');
			}

			// these are the only three places where we do not want to hide space name on collapse of side nav
			if (!isSpaceOverviewPage && !isVersionHistoryPage && !isAttachmentsPage) {
				if (getIsSideNavCollapsed()) {
					if (shouldListenForTitleVisibilityChanges) {
						if (isTitleFullyVisible) {
							// SIDE NAV COLLAPSED + TITLE IS SHOWING = ONLY SPACE NAME
							showOnlySpaceName();
						} else {
							if (contentTypesHeaderDataLoading) {
								hideEntireBreadcrumbs();
							} else {
								// SIDE NAV COLLAPSED + TITLE IS NOT SHOWING = FULL
								showFullBreadcrumbs();
							}
						}
					} else {
						if (isEditorToolbarShown) {
							// for when user swaps between contextual toolbar and docked toolbar
							showFullBreadcrumbs();
						}

						// for all other cases that don't care about title visibility, simply show space name and separator
						checkAndSetDisplayAttribute(separator, 'inline');
						checkAndSetDisplayAttribute(spaceName, 'inline');
					}
				} else {
					if (shouldListenForTitleVisibilityChanges) {
						if (isTitleFullyVisible) {
							if (isLegacyObjectSidebarShown) {
								// in the case where you are at the top of the page and the object sidebar is open, we should force show breadcrumbs and border so the sidebar isn't floating there by itself
								showOnlyContentTitle();
							} else {
								// SIDE NAV EXPANDED + TITLE IS SHOWING = NO BREADCRUMBS
								hideEntireBreadcrumbs();
							}
						} else {
							if (contentTypesHeaderDataLoading) {
								hideEntireBreadcrumbs();
							} else {
								// SIDE NAV EXPANDED + TITLE IS COVERED = CONTENT TITLE ONLY
								showOnlyContentTitle();
							}
						}
					} else {
						if (isEditorToolbarShown) {
							// for when user swaps between contextual toolbar and docked toolbar
							showOnlyContentTitle();
						}
						// for all other cases that don't care about title visibility, simply hide space name and separator
						checkAndSetDisplayAttribute(separator, 'none');
						checkAndSetDisplayAttribute(spaceName, 'none');
					}
				}
			}
		};

		window.addEventListener('resize', toggleSpaceNameBreadcrumbListener); // listen for side nav collapse and expand and other resize events
		if (!hasMounted.current) {
			hasMounted.current = true;
		} else {
			// don't run this on initial render of SSRed page since it will become a ttvc offender
			toggleSpaceNameBreadcrumbListener();
		}

		return () => {
			window.removeEventListener('resize', toggleSpaceNameBreadcrumbListener);
		};
	}, [
		isLegacyObjectSidebarShown,
		shouldListenForTitleVisibilityChanges,
		isSpaceOverviewPage,
		isVersionHistoryPage,
		isAttachmentsPage,
		contentId,
		isTitleFullyVisible,
		isEditorToolbarShown,
		contentTypesHeaderDataLoading,
	]);

	// NOTE: If you are changing any of the logic below for display modes, you will likely need to update the useEffect above to match accordingly.
	if (!isVersionHistoryPage && !isAttachmentsPage) {
		// all live pages, page, blogs, and editor except for space overview and archived pages
		if (
			(contentType === 'blogpost' || contentType === 'page') &&
			!isSpaceOverviewPage &&
			!isArchived
		) {
			if (contentTypesHeaderDataLoading && !isTitleFullyVisible) {
				return BREADCRUMBS_MODES.HIDDEN;
			}

			if (isEditorToolbarShown) {
				// don't care about title visibility
				if (getIsSideNavCollapsed()) {
					return BREADCRUMBS_MODES.FULL;
				} else {
					return BREADCRUMBS_MODES.ONLY_CONTENT_TITLE;
				}
			} else {
				// care about title visibility (aka view page or blogpost, live page view mode)
				if (getIsSideNavCollapsed()) {
					// SIDE NAV COLLAPSED + TITLE IS SHOWING = ONLY SPACE NAME
					if (isTitleFullyVisible) {
						return BREADCRUMBS_MODES.ONLY_SPACE_NAME;
					} else {
						// SIDE NAV COLLAPSED + TITLE IS NOT SHOWING = FULL
						return BREADCRUMBS_MODES.FULL;
					}
				} else {
					// SIDE NAV EXPANDED + TITLE IS SHOWING = NO BREADCRUMBS
					if (isTitleFullyVisible) {
						if (isLegacyObjectSidebarShown) {
							// in the case where you are at the top of the page and the object sidebar is open, we should force show breadcrumbs and border so the sidebar isn't floating there by itself
							return BREADCRUMBS_MODES.ONLY_CONTENT_TITLE;
						}
						return BREADCRUMBS_MODES.HIDDEN;
					} else {
						// SIDE NAV EXPANDED + TITLE IS COVERED = CONTENT TITLE ONLY
						return BREADCRUMBS_MODES.ONLY_CONTENT_TITLE;
					}
				}
			}
			// all other content types
		} else if (
			contentType === 'database' ||
			isCanvasContentType(contentType) ||
			contentType === 'embed'
		) {
			if (getIsSideNavCollapsed()) {
				return BREADCRUMBS_MODES.FULL;
			} else {
				return BREADCRUMBS_MODES.ONLY_CONTENT_TITLE;
			}
		}
	}

	return BREADCRUMBS_MODES.FULL;
};
